import { App as AntdApp, ConfigProvider, theme as antdTheme } from 'antd';
import { Suspense, useMemo } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { RouterProvider } from 'react-router-dom';

import GlobalLoader from '~/common/components/utils/GlobalLoader';
import Spinner from '~/common/components/utils/Spinner';
import ChunkErrorBoundary from '~/common/errors/ChunkErrorBoundary.ts';
import { store, useAppSelector } from '~/common/stores';
import { ThemeMode } from '~/common/types/theme.type';
import antDesignTheme from '~/configs/antDesignTheme';
import ThemeGlobalStyles from '~/configs/theme-css-vars';

import '~/common/assets/styles/global.css';

import router from '~/routes';

function AppShell() {
  const themeMode = useAppSelector(state => state.layout.themeMode);
  const isDark = themeMode === ThemeMode.DARK;

  const themeConfig = useMemo(
    () => ({
      ...antDesignTheme,
      cssVar: true,
      algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        ...antDesignTheme.token,
        colorPrimary: '#FF5A5F',
        // 다크 시에만 hex 값으로 조건부 override. 라이트는 antd 기본값 사용.
        // var(--color-xxx) 직접 대입을 피해 별도 React Root(예: dialog.util)에서도
        // antd 토큰이 hex로 정상 emit되도록 한다 (lt-console 동일 패턴).
        ...(isDark
          ? {
              colorBgContainer: '#1a1b1d',
              colorBgElevated: '#1a1b1d',
              colorBgLayout: '#0e0e10',
              colorBorder: '#4b4c4f',
              colorBorderSecondary: '#4b4c4f',
              colorText: '#f5f5f7',
              colorTextHeading: '#f5f5f7',
              colorTextSecondary: '#b0b1b4',
              colorTextTertiary: '#7f8083',
              colorTextDescription: '#b0b1b4',
              colorTextPlaceholder: '#7f8083',
              colorTextDisabled: '#7f8083',
            }
          : {}),
      },
    }),
    [isDark],
  );

  return (
    <ConfigProvider theme={themeConfig}>
      <ThemeGlobalStyles />
      <AntdApp>
        {/*
          최상위 Suspense 는 라우트 밖 lazy 로드를 위한 backstop 이다.
          라우트 전환의 청크 로딩 표시는 이 경계가 담당하지 않는다 — 이미 마운트된 트리는
          transition 내비게이션에서 fallback 으로 교체되지 않아 이전 화면이 그대로 잔존하기 때문.
          전환 인디케이터는 라우트별 경계(`~/routes` 의 lazyRoute)가 전담한다.
        */}
        <Suspense fallback={<Spinner fullScreen />}>
          <ChunkErrorBoundary>
            <RouterProvider router={router} />
          </ChunkErrorBoundary>
        </Suspense>
        <GlobalLoader />
      </AntdApp>
    </ConfigProvider>
  );
}

function App() {
  return (
    <ReduxProvider store={store}>
      <AppShell />
    </ReduxProvider>
  );
}

export default App;
